import { HttpErrorResponse } from "@angular/common/http";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
} from "@angular/forms";
import { NgbDateParserFormatter } from "@ng-bootstrap/ng-bootstrap";
import { AlertController, ModalController } from "ionic-angular";
import moment from "moment";
import CollapsibleEvent from "../../../../models/events/collapsible-modal.event";
import { BillingBenefitCode } from "../../../../models/payers/payer";
import { PayersService } from "../../../../providers/payers-service/payers.service";
import { UtilityService } from "../../../../providers/utility-service/utility-service";
import { NgbCustomParserFormatter } from "../../../common/date-picker/ngb-custom-parser-formatter";

@Component({
  selector: "billing-codes-hospice-visits",
  templateUrl: "hospice-visits.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BillingCodesHospiceVisits),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => BillingCodesHospiceVisits),
      multi: true,
    },
    { provide: NgbDateParserFormatter, useClass: NgbCustomParserFormatter },
  ],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class BillingCodesHospiceVisits implements OnInit, OnDestroy {
  @Input() currentPayer: any;
  @Input() currentPayerType: any;
  @Output() onCanceled = new EventEmitter<any>();
  @Output() setCheckMark = new EventEmitter<any>();

  billingCodes: FormGroup;
  contractPeriodsOptions: any[] = [];
  selectedContractPeriod: any;
  useMedicareBillingCodes: boolean = false;
  showLevelOfCareDiv = false;
  showVisitsDiv: boolean = false;
  billingCodesVisitData: BillingBenefitCode[] = [];
  saveEnabled: true;
  dateFormat: string = "YYYY-MM-DD";
  dateUIFormat: string = "MM/DD/YYYY";
  dateRegex = new RegExp("\\d{4}-\\d{2}-\\d{2}");
  periodsResponse: any = [];
  minDateFormat: any = moment("2023-09-30", this.dateFormat);
  periodResponse: any = {};
  benefitSubtype: string = "VISITS";
  benefitType: string = "HOSPICE";
  editMode: boolean = false;
  showHCPCSCodes: boolean = false;
  validateModFields: boolean = true;
  codesEditDisabled: boolean = false;

  hospiceVisitBillingCodesSetup: BillingBenefitCode[] = [];

  private payerType: string;

  constructor(
    private utilityService: UtilityService,
    private payersService: PayersService,
    private alertController: AlertController,
    private modalCtrl: ModalController,
    private fb: FormBuilder
  ) {}

  ngOnDestroy(): void {}

  setupBaseData() {
    this.hospiceVisitBillingCodesSetup = [
      {
        RevenueCode: "",
        HcpcsCode: "",
        Description: "RN Visit",
        Service: "RN",
        M1: "",
        M2: "",
        M3: "",
        M4: "",
      },
      {
        RevenueCode: "",
        HcpcsCode: "",
        Description: "LVN/LPN Visit",
        Service: "LN",
        M1: "",
        M2: "",
        M3: "",
        M4: "",
      },
      {
        RevenueCode: "",
        HcpcsCode: "",
        Description: "Social Worker Visit",
        Service: "SW",
        M1: "",
        M2: "",
        M3: "",
        M4: "",
      },
      {
        RevenueCode: "",
        HcpcsCode: "",
        Description: "Social Worker Phone Call",
        Service: "SW-PHONECALL",
        M1: "",
        M2: "",
        M3: "",
        M4: "",
      },
      {
        RevenueCode: "",
        HcpcsCode: "",
        Description: "Hospice Aide Visit",
        Service: "HA",
        M1: "",
        M2: "",
        M3: "",
        M4: "",
      },
    ];
  }

  ngOnInit(): void {
    this.showLevelOfCareDiv = true;
    this.useMedicareBillingCodes = false;
    this.billingCodesVisitData = [...this.hospiceVisitBillingCodesSetup];
    this.loadBenefitPeriods(this.benefitType, this.benefitSubtype, true);
    if (this.currentPayer && this.currentPayer.payerId) {
      this.payerType = this.currentPayer.payerType;
    }
    this.initForm();
    this.setupBaseData();
  }

  onPeriodChanged() {
    this.minDateFormat = moment("2023-09-30", this.dateFormat);
    this.loadBenefitPeriods(this.benefitType, this.benefitSubtype, true);
    this.selectedContractPeriod = null;
    this.billingCodesVisitData = [];
  }

  initForm() {
    this.billingCodes = this.fb.group({
      city: [],
      period: "10/01/2023",
    });
  }

  public validateMod(data) {
    this.validateModFields = data;
  }

  loadDefaults() {
    this.selectedContractPeriod = null;
    this.billingCodesVisitData = [];
    this.billingCodesVisitData = [...this.hospiceVisitBillingCodesSetup];
    this.useMedicareBillingCodes = false;
  }

  async onContractPeriodChange(event: any) {
    if (!event) {
      this.loadDefaults();
      this.showVisitsDiv = false;
      this.showHCPCSCodes = true;
      return;
    }
    if (event.id === 0) {
      this.showVisitsDiv = true;
      this.showHCPCSCodes = true;
      let contractPeriodModal = this.modalCtrl.create(
        "ContractPeriodModalComponent",
        { minDateFormat: this.minDateFormat },
        { cssClass: "contract-period-modal" }
      );
      contractPeriodModal.onDidDismiss((data) => {
        if (data) {
          let newContractPeriod = moment(new Date(data)).format(
            this.dateUIFormat
          );
          let newEndContractPeriod = moment(new Date(newContractPeriod))
            .subtract(1, "days")
            .format(this.dateUIFormat);
          const newOption = {
            id: this.contractPeriodsOptions.length,
            name: moment(new Date(data)).format(this.dateUIFormat),
            startDate: moment(data).format(this.dateFormat),
            endDate: null,
          };
          this.useMedicareBillingCodes = true;
          this.medicareCheckBoxChanged();
          this.contractPeriodsOptions = [
            ...this.contractPeriodsOptions,
            newOption,
          ];
          this.sortBenefitCodesPeriods();
          this.selectedContractPeriod = newOption;
          this.contractPeriodsOptions.shift();
          this.billingCodesVisitData = [];
          this.setupBaseData();
          this.billingCodesVisitData = [...this.hospiceVisitBillingCodesSetup];
          this.editMode = false;
        } else {
          this.loadDefaults();
        }
      });

      contractPeriodModal.present();
    } else {
      this.selectedContractPeriod = event;
      this.selectedContractPeriod.startDate = moment(event.startDate).format(
        this.dateUIFormat
      );
      this.selectedContractPeriod.endDate = event.endDate
        ? moment(event.endDate).format(this.dateUIFormat)
        : "";
      this.utilityService.increaseSpinnerCount("Loading");
      this.showVisitsDiv = true;
      this.showHCPCSCodes = true;
      this.loadBenefitPeriod(event);
      if (this.currentPayerType != "MEDICARE") {
        let filteredContractPeriods = this.contractPeriodsOptions.filter(
          (option) => option.startDate != this.selectedContractPeriod.startDate
        );
        if (filteredContractPeriods.length == 0) {
          this.useMedicareBillingCodes = true;
          this.medicareCheckBoxChanged();
        }
      }
    }
  }

  public onCollapseClicked($e: CollapsibleEvent): void {
    if ($e.controlId === "level-of-care") {
      this.showLevelOfCareDiv = $e.isOpen;
      this.showVisitsDiv = !$e.isOpen;
    } else if ($e.controlId === "visits") {
      this.showVisitsDiv = $e.isOpen;
      this.showLevelOfCareDiv = !$e.isOpen;
    }
  }

  public saveCodes(): void {
    let request = this.createRequest();
    this.utilityService.increaseSpinnerCount("Loading");
    this.editMode = true;
    this.payersService.postPayerBenefitCodes(request).subscribe(() => {
      setTimeout(() => {
        this.utilityService.decreaseSpinnerCount();
        let newOptionIndex = this.contractPeriodsOptions.findIndex(
          (option) => option.id === 0
        );
        this.loadBenefitPeriods(
          this.benefitType,
          this.benefitSubtype,
          newOptionIndex === -1
        );

        if (newOptionIndex === -1) this.addNewPeriodOption();
        this.editMode = false;
        this.showAlert({
          title: "Success",
          message: "Billing Codes saved.",
        });
        this.setCheckMark.emit();
      }, 2000);
    }, this.onSaveError);
  }

  showSavePayerModal() {
    if (
      this.currentPayer != null &&
      this.currentPayer.payerId &&
      this.selectedContractPeriod &&
      this.selectedContractPeriod.id > 0
    ) {
      let modalPage = this.modalCtrl.create(
        "SaveFacilityPage",
        {
          title: "Payer Information Change",
          commentPlaceholderKey: "archiveFacilityCommentPlaceHolder",
          popupMessageKey: "savePayerWarningMessage",
          section: "payer",
        },
        {
          enableBackdropDismiss: false,
          cssClass: "save-payer-confirmation-popup",
        }
      );
      modalPage.onDidDismiss((data) => {
        if (data.save) {
          this.saveCodes();
        }
      });
      modalPage.present();
    } else {
      this.saveCodes();
    }
  }

  addNewPeriodOption(): void {
    this.contractPeriodsOptions = [
      { id: 0, name: "Add New" },
      ...this.contractPeriodsOptions,
    ];
  }

  showAlert = ({ title, message }) => {
    let alert = this.alertController.create({
      title,
      message,
      buttons: [
        {
          text: "Ok",
        },
      ],
    });
    alert.present();
  };

  private onSaveError = (error) => {
    this.saveEnabled = true;
    this.utilityService.decreaseSpinnerCount();
    if (error instanceof HttpErrorResponse) {
      if (error.error) {
        this.showAlert({
          title: "Error",
          message: error.error.message,
        });
      } else {
        this.showAlert({
          title: "Error",
          message: "Error while saving the rates. " + error.message,
        });
      }
    }
  };

  public createRequest(): any {
    let endDate = this.selectedContractPeriod
      ? moment(this.selectedContractPeriod.endDate).format(this.dateFormat)
      : null;
    if (!this.dateRegex.test(endDate)) endDate = null;
    let body = {
      payerId: this.currentPayer.payerId,
      dataType: "PayerServiceCodes",
      payerType: this.payerType,
      Start: moment(this.selectedContractPeriod.startDate).format(
        this.dateFormat
      ),
      End: endDate,
      BenefitType: this.benefitType,
      benefitSubType: this.benefitSubtype,
      UseDefaultCodes: this.useMedicareBillingCodes,
      Codes: {},
    };
    this.billingCodesVisitData.map((bc) => {
      if (bc.Service == "SW-PHONECALL") {
        body.Codes["SW-PHONECALL"] = this.createNewBillingBenefitCode(bc);
        body.Codes["SW-PHONECALL"].Service = "SW";
      } else {
        body.Codes[bc.Service] = this.createNewBillingBenefitCode(bc);
      }
    });
    return body;
  }

  createNewBillingBenefitCode(bc: BillingBenefitCode): BillingBenefitCode {
    return {
      Description: bc.Description,
      HcpcsCode: bc.HcpcsCode,
      M1: bc.M1,
      M2: bc.M2,
      M3: bc.M3,
      M4: bc.M4,
      RevenueCode: bc.RevenueCode,
      Service: bc.Service,
    };
  }

  loadBenefitPeriods(payerType, subType, addNewOption) {
    let payerId = this.currentPayer.payerId;
    let payerTypeValue = payerType;
    let payerSubtype = subType;

    if (this.currentPayerType == "MEDICARE") {
      payerId = "MEDICARE";
    }
    this.payersService
      .getPayerBenefitCodesPeriods(payerId, payerTypeValue, payerSubtype)
      .subscribe((response: any) => {
        this.periodsResponse = response;
        this.contractPeriodsOptions = [];
        this.mapBenefitPeriods(response, addNewOption);
        this.filterPeriodOptions();
        this.sortBenefitCodesPeriods();
      });
  }

  mapBenefitPeriods(response: any, addNew: boolean) {
    if (addNew) this.addNewPeriodOption();
    response.map((period) => {
      let name = moment(period.start, this.dateFormat).format(
        this.dateUIFormat
      );
      let sDate = moment(period.start).format(this.dateFormat);
      let minDate = moment(this.minDateFormat).format(this.dateFormat);
      if (moment(sDate).isAfter(moment(minDate))) {
        this.minDateFormat = period.start;
      }
      let endDate = null;
      if (period.end && this.dateRegex.test(period.end)) endDate = period.end;

      if (endDate) {
        name += "-" + moment(period.end).format(this.dateUIFormat);
      }

      this.contractPeriodsOptions.push({
        id:
          this.contractPeriodsOptions.length == 0
            ? 1
            : this.contractPeriodsOptions.length + 1,
        name: name,
        startDate: period.start,
        endDate: endDate,
      });
    });
  }

  public validateFields() {
    return (
      this.currentPayer != null &&
      this.currentPayer.payerId &&
      this.selectedContractPeriod &&
      this.selectedContractPeriod.id > 0 &&
      this.validateCodeFields(this.billingCodesVisitData) &&
      this.validateModFields
    );
  }

  public cancel(): void {
    this.contractPeriodsOptions = [];
    this.onCanceled.emit(true);
  }

  sortBenefitCodesPeriods() {
    this.contractPeriodsOptions = this.contractPeriodsOptions.sort((a, b) => {
      if (!a.startDate) return 0;
      if (!b.startDate) return 0;
      if (moment(a.startDate) > moment(b.startDate)) return -1;
      return 0;
    });
  }

  public validateCodeFields(configuration) {
    for (let i = 0; i < configuration.length; i++) {
      let config = configuration[i];
      if (
        config.RevenueCode == null ||
        config.RevenueCode === "" ||
        config.RevenueCode.length < 4 ||
        config.HcpcsCode == null ||
        config.HcpcsCode === "" ||
        config.HcpcsCode.length < 5
      )
        return false;
    }
    return true;
  }

  public loadBenefitPeriod(benefitPeriod) {
    let payerId = this.currentPayer.payerId;
    let benefitType = this.benefitType;
    let benefitSubtype = this.benefitSubtype;
    let startDate = moment(benefitPeriod.startDate).format(this.dateFormat);
    let editMode = false;

    if (this.currentPayerType == "MEDICARE") {
      payerId = "MEDICARE";
      this.showVisitsDiv = false;
      editMode = true;
    }
    this.payersService
      .getPayerBenefitCodesByPeriod(
        payerId,
        benefitType,
        benefitSubtype,
        startDate
      )
      .subscribe(
        (result: any) => {
          this.utilityService.decreaseSpinnerCount();
          this.periodResponse = result[0];
          if (this.periodResponse) {
            this.editMode = editMode;
            this.billingCodesVisitData = [];
            this.mapPeriodResponse();
            this.useMedicareBillingCodes = this.periodResponse.useDefaultCodes;
          } else {
            this.billingCodesVisitData = [
              ...this.hospiceVisitBillingCodesSetup,
            ];
          }
        },
        () => {
          this.billingCodesVisitData = [];
          this.billingCodesVisitData = [...this.hospiceVisitBillingCodesSetup];
        }
      );
  }

  mapPeriodResponse() {
    for (var key in this.periodResponse.codes) {
      if (!this.periodResponse.codes.hasOwnProperty(key)) continue;
      var obj = this.periodResponse.codes[key];
      let newPeriod: BillingBenefitCode = {
        RevenueCode: obj.revenueCode,
        HcpcsCode: obj.hcpcsCode,
        Description: this.mapFullDescription(obj.description),
        Service: key,
        M1: obj.m1,
        M2: obj.m2,
        M3: obj.m3,
        M4: obj.m4,
      };
      this.billingCodesVisitData.push(newPeriod);
    }

    const order = [
      "RN Visit",
      "LVN/LPN Visit",
      "Social Worker Visit",
      "Social Worker Phone Call",
      "Hospice Aide Visit",
    ];

    this.billingCodesVisitData = this.billingCodesVisitData.sort((a, b) => {
      const indexA = order.indexOf(a.Description);
      const indexB = order.indexOf(b.Description);
      return indexA - indexB;
    });
  }

  mapFullDescription(description) {
    switch (description) {
      case "SW Visit":
        return "Social Worker Visit";
      case "SW Phone Call":
        return "Social Worker Phone Call";
      case "HA Visit":
        return "Hospice Aide Visit";
      default:
        return description;
    }
  }

  filterPeriodOptions() {
    if (this.currentPayerType == "MEDICARE") {
      this.contractPeriodsOptions = this.contractPeriodsOptions.filter(
        (option) => option.name != "Add New"
      );
    }
  }

  getEffectiveDate() {
    return this.selectedContractPeriod && this.selectedContractPeriod.startDate
      ? this.formatDate(this.selectedContractPeriod.startDate)
      : "";
  }

  getExpiredDate() {
    return this.selectedContractPeriod && this.selectedContractPeriod.endDate
      ? this.formatDate(this.selectedContractPeriod.endDate)
      : "";
  }

  formatDate(dateString: string): string {
    return moment(dateString).format(this.dateUIFormat);
  }

  async medicareCheckBoxChanged() {
    if (this.useMedicareBillingCodes) {
      await this.getMedicareDefaultValues();
    } else {
      if (this.selectedContractPeriod) {
        let payerId = this.currentPayer.payerId;
        let benefitType = this.benefitType;
        let benefitSubtype = this.benefitSubtype;
        let startDate = moment(this.selectedContractPeriod.startDate).format(
          this.dateFormat
        );
        this.utilityService.increaseSpinnerCount("Loading");
        this.payersService
          .getPayerBenefitCodesByPeriod(
            payerId,
            benefitType,
            benefitSubtype,
            startDate
          )
          .subscribe(
            async (result: any) => {
              this.utilityService.decreaseSpinnerCount();
              this.periodResponse = result[0];
              if (this.periodResponse) {
                this.mapPeriodResponseMedicareCheckbox();
              } else {
                await this.getMedicareDefaultValues();
              }
              this.codesEditDisabled = false;
              this.editMode = false;
            },
            () => {
              this.billingCodesVisitData = [];
              this.billingCodesVisitData = [
                ...this.hospiceVisitBillingCodesSetup,
              ];
            }
          );
      }
    }
  }

  async getMedicareDefaultValues() {
    this.utilityService.increaseSpinnerCount("Loading Medicare Billing Codes");
    let medicareContractPeriods = (await this.getMedicareContractPeriods()) as {
      start: string;
      end: string;
    }[];
    if (medicareContractPeriods && medicareContractPeriods.length > 0) {
      let activeMedicarePeriod = medicareContractPeriods
        .filter((period) => {
          let endDate = period["end"] == null ? null : new Date(period["end"]);
          let startDate = new Date(period["start"]);
          let selectedPeriodDate = new Date(
            this.selectedContractPeriod.startDate
          );
          if (
            startDate <= selectedPeriodDate &&
            (endDate == null || endDate >= selectedPeriodDate)
          ) {
            return period;
          }
          return null;
        })
        .pop();
      if (activeMedicarePeriod) {
        let activeMedicarePeriodBillingCodes =
          await this.getActiveMedicareContractPeriod(
            "MEDICARE",
            activeMedicarePeriod.start
          );
        if (activeMedicarePeriodBillingCodes) {
          this.periodResponse = activeMedicarePeriodBillingCodes[0];
          this.mapPeriodResponseMedicareCheckbox();
          this.codesEditDisabled = false;
          this.editMode = false;
        } else {
          this.showAlert({
            title: "Config Not Found",
            message: `Could not find Medicare Billing Codes configuration for: ${moment(
              this.selectedContractPeriod.startDate
            ).format(this.dateUIFormat)}.`,
          });
          this.billingCodesVisitData = [...this.hospiceVisitBillingCodesSetup];
        }
      } else {
        this.showAlert({
          title: "Config Not Found",
          message: `Could not find Medicare Billing Codes configuration for: ${moment(
            this.selectedContractPeriod.startDate
          ).format(this.dateUIFormat)}.`,
        });
        this.billingCodesVisitData = [...this.hospiceVisitBillingCodesSetup];
      }
      this.utilityService.decreaseSpinnerCount();
    }
  }

  async getMedicareContractPeriods() {
    return new Promise((resolve, reject) => {
      this.payersService
        .getPayerBenefitCodesPeriods(
          "MEDICARE",
          this.benefitType,
          this.benefitSubtype
        )
        .subscribe(
          (response: any) => {
            if (response) {
              resolve(response);
            } else {
              resolve();
            }
          },
          (err) => {
            reject();
          }
        );
    });
  }

  async getActiveMedicareContractPeriod(payerId, startDate) {
    return new Promise((resolve, reject) => {
      this.payersService
        .getPayerBenefitCodesByPeriod(
          payerId,
          this.benefitType,
          this.benefitSubtype,
          startDate
        )
        .subscribe(
          (result: any) => {
            if (result) {
              resolve(result);
            } else {
              resolve();
            }
          },
          (err) => {
            reject();
          }
        );
    });
  }

  mapPeriodResponseMedicareCheckbox() {
    let newData = [];
    for (var key in this.periodResponse.codes) {
      if (!this.periodResponse.codes.hasOwnProperty(key)) continue;
      var obj = this.periodResponse.codes[key];
      let oldValue = this.billingCodesVisitData.find(
        (value) => value.Description == obj.description
      );
      let newPeriod: BillingBenefitCode = {
        RevenueCode: obj.revenueCode,
        HcpcsCode: obj.hcpcsCode,
        Description: this.mapFullDescription(obj.description),
        Service: key,
        M1: oldValue ? oldValue.M1 : obj.m1,
        M2: oldValue ? oldValue.M2 : obj.m2,
        M3: oldValue ? oldValue.M3 : obj.m3,
        M4: oldValue ? oldValue.M4 : obj.m4,
      };

      newData.push(newPeriod);
    }

    this.billingCodesVisitData = [];
    this.billingCodesVisitData = newData;

    const order = [
      "RN Visit",
      "LVN/LPN Visit",
      "Social Worker Visit",
      "Social Worker Phone Call",
      "Hospice Aide Visit",
    ];

    this.billingCodesVisitData = this.billingCodesVisitData.sort((a, b) => {
      const indexA = order.indexOf(a.Description);
      const indexB = order.indexOf(b.Description);
      return indexA - indexB;
    });
  }

  protected readonly event = event;
}



// WEBPACK FOOTER //
// ./src/components/payers/billing-codes/hospice-visits/hospice-visits.ts